import { useCallback } from 'react';
import { DropTargetMonitor } from 'react-dnd';
import { useEditorStore, ILayoutComponent } from '@diana/core';
import {
  canMoveLayoutComponent,
  IDndLayoutComponentLocation as ILocation,
  IDndLayoutComponentDragObject as IDragObject,
  IDndLayoutComponentDropResult as IDropResult,
} from '../../../../../providers/DndLayoutComponents2';
import { isEqual } from 'lodash';
import { useSyncComponentNameAndDataPath } from '../../../Support/useSyncComponentNameAndDataPath';

interface IUseDndComponentHoverEventProps {
  layoutId: string;
  taskId: string | null;
  stepId: string | null;
  flowId: string | null;
}

interface IDndComponentHoverEventData {
  dragObject: IDragObject;
  targetElement: HTMLElement | null;
  targetDropResult: IDropResult;
  monitor: DropTargetMonitor<IDragObject, IDropResult>;
}

export const useDndComponentHoverEvent = (props: IUseDndComponentHoverEventProps) => {
  const { layoutId, taskId, stepId, flowId } = props;

  const getLayoutById = useEditorStore((s) => s.getLayoutById);
  const updateLayoutById = useEditorStore((s) => s.updateLayoutById);
  const setTemporaryLayoutComponentId = useEditorStore((s) => s.setTemporaryLayoutComponentId);

  const syncComponentNameAndDataPath = useSyncComponentNameAndDataPath({
    layoutId,
    taskId,
    stepId,
    flowId,
  });

  const moveComponent = useCallback(
    (args: IDndComponentHoverEventData): ILocation | null => {
      const { dragObject, targetDropResult } = args;

      const sourceLayout = dragObject.location ? getLayoutById(dragObject.location.layoutId) : null;
      const destinationLayout = getLayoutById(targetDropResult.location.layoutId);

      const descendants = dragObject.descendants;
      const descendantsMap = descendants.reduce<Record<string, ILayoutComponent>>(
        (map, descendant) => ({ ...map, [descendant.id]: descendant }),
        {},
      );

      // set temporary
      if (destinationLayout && dragObject.origin?.layoutId !== destinationLayout.id) {
        setTemporaryLayoutComponentId(dragObject.component.id, true);
        for (const descendant of dragObject.descendants) {
          setTemporaryLayoutComponentId(descendant.id, true);
        }
      }

      // has source and destination layout
      if (dragObject.location && sourceLayout && destinationLayout) {
        const sourceLocation = dragObject.location;
        const targetLocation = targetDropResult.location;

        // same layout
        if (sourceLayout.id === destinationLayout.id && !isEqual(sourceLocation, targetLocation)) {
          const location = { ...targetDropResult.location };
          updateLayoutById(sourceLayout.id, (draft) => {
            draft.components.splice(sourceLocation.index, 1);

            const index = targetLocation.index;
            const parentId = targetLocation.parentId;
            const component = syncComponentNameAndDataPath({ ...dragObject.component, parentId });
            const otherComponents = descendants.map(syncComponentNameAndDataPath);

            draft.components.splice(index, 0, component);

            if (otherComponents.length) {
              draft.components = draft.components.filter((c) => !descendantsMap[c.id]);
              location.index = draft.components.findIndex((c) => c.id === dragObject.component.id);
              draft.components.splice(location.index + 1, 0, ...otherComponents);
            }

            return draft;
          });

          return location;
        }

        // different layout
        if (sourceLayout.id !== destinationLayout.id) {
          updateLayoutById(destinationLayout.id, (draft) => {
            const index = targetLocation.index;
            const parentId = targetLocation.parentId;
            const component = syncComponentNameAndDataPath({ ...dragObject.component, parentId });
            const otherComponents = descendants.map(syncComponentNameAndDataPath);

            draft.components.splice(index, 0, component, ...otherComponents);

            return draft;
          });

          updateLayoutById(sourceLayout.id, (draft) => {
            draft.components.splice(sourceLocation.index, 1);

            if (descendants.length) {
              draft.components = draft.components.filter((c) => !descendantsMap[c.id]);
            }

            return draft;
          });

          return targetDropResult.location;
        }
      }

      // no source but have destination layout
      if (!dragObject.location && destinationLayout) {
        updateLayoutById(destinationLayout.id, (draft) => {
          const { index, parentId } = targetDropResult.location;

          const component = syncComponentNameAndDataPath({ ...dragObject.component, parentId });
          const otherComponents = descendants.map(syncComponentNameAndDataPath);

          draft.components.splice(index, 0, component, ...otherComponents);

          return draft;
        });
        return targetDropResult.location;
      }

      // reset temporary
      if (destinationLayout && dragObject.origin?.layoutId === destinationLayout.id) {
        setTemporaryLayoutComponentId(dragObject.component.id, false);
        for (const descendant of dragObject.descendants) {
          setTemporaryLayoutComponentId(descendant.id, false);
        }
      }

      return null;
    },
    [getLayoutById, updateLayoutById, setTemporaryLayoutComponentId, syncComponentNameAndDataPath],
  );

  const onDndComponentHover = useCallback(
    (args: IDndComponentHoverEventData) => {
      const { dragObject, targetDropResult, targetElement, monitor } = args;
      const targetLayout = getLayoutById(targetDropResult.location.layoutId);

      if (monitor.canDrop()) {
        const canMove = canMoveLayoutComponent({
          dragObject,
          targetLayout,
          targetElement,
          targetDropResult,
          clientOffset: monitor.getClientOffset(),
        });

        if (canMove) {
          const location = moveComponent(args);
          if (location) {
            const layout = getLayoutById(location.layoutId);
            const index =
              layout?.components.findIndex((c) => c.id === dragObject.component.id) ?? -1;
            dragObject.location = { ...location, index: index >= 0 ? index : location.index };
          }
        }
      }
    },
    [getLayoutById, moveComponent],
  );

  return { onDndComponentHover };
};
